import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PlusCircle, Trash2, Pencil } from "lucide-react";
import { AddCommentModal } from "@/components/candidates/AddCommentModal";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";
import { CommentContent } from "@/components/comments/CommentContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface JobCommentsProps {
  jobId: string | undefined;
}

interface JobComment {
  id: string;
  content: string;
  created_by: string;
  created_by_uuid: string | null;
  created_at: string;
  updated_at: string;
  migration?: boolean;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

export const JobComments = ({ jobId }: JobCommentsProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [comments, setComments] = useState<JobComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddCommentOpen, setIsAddCommentOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<JobComment | null>(null);
  const [commentToEdit, setCommentToEdit] = useState<JobComment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchComments = async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('job_comments')
        .select(`
          *,
          profiles:created_by_uuid (
            first_name,
            last_name
          )
        `)
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setComments(data || []);
    } catch (error) {
      console.error('Error fetching job comments:', error);
      toast({
        title: "Error",
        description: "Could not load comments",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [jobId]);

  const handleAddComment = async (content: string) => {
    if (!jobId || !content || !user?.id) return;

    try {
      const { data, error } = await supabase
        .from('job_comments')
        .insert({
          job_id: jobId,
          content,
          created_by: "Current User", // Keep for backward compatibility
          created_by_uuid: user.id, // New UUID field
        })
        .select();

        // also trigger a call to ai-processing
        const { data: analysisData, error: analysisError } = await supabase.functions.invoke(
            'ai-processing?command=push-new-analysis',
            {
                body: {
                    entityId: jobId,
                    entityType: "job",
                    analysisType: "analyse-job-offer",
                    actionOnComplete: "update-job",
                }
            }
        );

      if (error) throw error;

      toast({
        title: "Success",
        description: "Comment added successfully",
      });

      // Refresh comments
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Could not add comment",
        variant: "destructive",
      });
    }
  };

  const handleEditComment = async (content: string) => {
    if (!commentToEdit || !jobId || !content) return;

    try {
      const { error } = await supabase
        .from('job_comments')
        .update({ 
          content,
          updated_at: new Date().toISOString()
        })
        .eq('id', commentToEdit.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Comment updated successfully",
      });

      // Refresh comments
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
      toast({
        title: "Error",
        description: "Could not update comment",
        variant: "destructive",
      });
    } finally {
      setCommentToEdit(null);
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete || !jobId) return;

    try {
      setIsDeleting(true);
      
      const { error } = await supabase
        .from('job_comments')
        .delete()
        .eq('id', commentToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Comment deleted successfully",
      });

      // Remove comment from local state
      setComments(prev => prev.filter(comment => comment.id !== commentToDelete.id));
      setCommentToDelete(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: "Error",
        description: "Could not delete comment",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getUserDisplayName = (comment: JobComment) => {
    // Check if this is a migrated comment
    if (comment.migration === true) {
      return {
        name: "Import",
        isItalic: true
      };
    }
    
    let displayName = "Unknown User";
    
    // First try to get name from joined profile data
    if (comment.profiles) {
      const fullName = `${comment.profiles.first_name || ''} ${comment.profiles.last_name || ''}`.trim();
      if (fullName) displayName = fullName;
    }
    
    // Fall back to the old created_by field for backward compatibility
    if (displayName === "Unknown User" && comment.created_by && comment.created_by !== "Current User") {
      displayName = comment.created_by;
    }
    
    return {
      name: displayName,
      isItalic: false
    };
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{t("companies.comments.title", "Comments")}</h3>
        <Button 
          onClick={() => setIsAddCommentOpen(true)}
          className="flex items-center gap-1"
        >
          <PlusCircle className="h-4 w-4" />
          <span>{t("companies.comments.addComment", "Add Comment")}</span>
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <>
          {comments.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 italic text-center py-8">{t("companies.comments.noComments", "No comments yet")}</p>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-sm">
                  <div className="flex justify-between mb-2">
                    <span className={`font-medium ${getUserDisplayName(comment).isItalic ? 'italic' : ''}`}>
                      {getUserDisplayName(comment).name}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCommentToEdit(comment)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-blue-500"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCommentToDelete(comment)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <CommentContent content={comment.content} />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <AddCommentModal
        isOpen={isAddCommentOpen}
        onClose={() => setIsAddCommentOpen(false)}
        onSave={handleAddComment}
      />

      <AddCommentModal
        isOpen={!!commentToEdit}
        onClose={() => setCommentToEdit(null)}
        onSave={handleEditComment}
        initialContent={commentToEdit?.content || ""}
        isEditing={true}
      />

      <AlertDialog open={!!commentToDelete} onOpenChange={(open) => !open && setCommentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteComment}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
